import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, useOutletContext, useLocation } from 'react-router-dom';
import Footer from '../components/Footer';
import useAutorizzazioni from '../hooks/useAutorizzazioni';
import useNetworkHierarchy from '../hooks/useNetworkHierarchy';
import useBranding from '../hooks/useBranding';
import api from '../services/api';
import { 
  ChevronDownIcon,
  UserCircleIcon,
  ArrowLeftOnRectangleIcon,
  Cog6ToothIcon,
  KeyIcon,
  UsersIcon,
  CalculatorIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  CurrencyEuroIcon,
  ShoppingCartIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  Bars3Icon,
  XMarkIcon,
  BellIcon,
  ArrowRightIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';

// Importa i componenti clienti
import ClientList from '../components/ClientList';
import ClientForm from '../components/ClientForm';
import ClientDetails from '../components/ClientDetails';

// Importa i componenti veicoli
import VehicleList from '../components/VehicleList';

// Importa società di noleggio
import SocietaNoleggioList from '../components/SocietaNoleggioList';
import SocietaNoleggioGaranzie from '../components/SocietaNoleggioGaranzie';

// Importa ScadenzeOfferteDropdown
import ScadenzeOfferteDropdown from '../components/ScadenzeOfferteDropdown';

// Importa i nuovi componenti
import OffertePublicPage from './OffertePublicPage';
import PreventivatorePage from './PreventivatorePage';
import GestioneCampiPage from './GestioneCampiPage';
import GuidaPage from './GuidaPage';
import MarketingPage from './MarketingPage';
import LeadPage from './LeadPage';
import NewLeadPage from './NewLeadPage';
import GestioneFontiProvenienza from './GestioneFontiProvenienza';
import HomeBanner from '../components/HomeBanner';

// Importa la pagina Rete Multilivello
import ReteMultilivelloPage from './ReteMultilivelloPage';

// Importa la pagina Gestione Utenti
import GestioneUtentiPage from './GestioneUtentiPage';

// Importa la pagina Gestione Ruoli
import GestioneRuoliPage from './GestioneRuoliPage';

// Importa la pagina Pratiche
import PratichePage from './PratichePage';
import PraticaDetailPageModulare from './PraticaDetailPageModulare';
import GestioneDocumentiPage from './GestioneDocumentiPage';

// Importa la pagina Contratti
import ContrattiPage from './ContrattiPage';

// Importa le pagine Statistiche
import StatisticheFatturatoPage from './StatisticheFatturatoPage';
import StatistichePratichePage from './StatistichePratichePage';

// Importa la pagina Provvigioni Contratti
import ProvvigioniContrattiPage from './ProvvigioniContrattiPage';

// Importa la pagina Profilo
import ProfiloPage from './ProfiloPage';
import QuotazioniPage from './QuotazioniPage';

// Importa la nuova pagina offerte vetture
import GestioneOfferteVetturePage from './GestioneOfferteVetturePage';
import GestioneCategoriePage from './GestioneCategoriePage';

// Importa il componente di protezione route
import ProtectedRoute from '../components/ProtectedRoute';

// Importa il componente dashboard NLT
import NltDashboard from '../components/NltDashboard';

const NewDashboard = () => {
  const { user, logout } = useOutletContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isNotificheMenuOpen, setIsNotificheMenuOpen] = useState(false);
  const [isNotificheModalOpen, setIsNotificheModalOpen] = useState(false);
  const [notifiche, setNotifiche] = useState([]);
  const [filtroNotifiche, setFiltroNotifiche] = useState('tutte'); // 'tutte', 'non_lette', 'lette'
  const [isClientiMenuOpen, setIsClientiMenuOpen] = useState(false);
  const [isQuotazioniMenuOpen, setIsQuotazioniMenuOpen] = useState(false);
  const [isDocumentiMenuOpen, setIsDocumentiMenuOpen] = useState(false);
  const [isReteMenuOpen, setIsReteMenuOpen] = useState(false);
  const [isStatisticheMenuOpen, setIsStatisticheMenuOpen] = useState(false);
  const [isGestioneMenuOpen, setIsGestioneMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Hook per le autorizzazioni
  const { 
    isPaginaAbilitata, 
    isSottopaginaAbilitata, 
    loading: authLoading,
    ruoloMappato,
    autorizzazioni,
    error
  } = useAutorizzazioni(user);

  // Hook per la gerarchia di rete (necessario per il branding)
  const { userNode, allNodes } = useNetworkHierarchy(user);

  // Hook per il branding dell'agenzia
  const { branding, getLogo, hasCustomLogo } = useBranding(user, userNode, allNodes);

  // Carica le notifiche
  useEffect(() => {
    const fetchNotifiche = async () => {
      try {
        const response = await api.get('/notifiche');
        if (response.data && response.data.success) {
          const data = response.data.data;
          // Combina scadenze offerte e pratiche in un unico array,
          // escludendo le notifiche del modulo bici (hanno la loro campanella su /bike)
          const tutteNotifiche = [
            ...(data.scadenzeOfferte || []),
            ...(data.pratiche || [])
          ].filter(n => n.datiAggiuntivi?.modulo !== 'bici');
          // Ordina per data più recente
          tutteNotifiche.sort((a, b) => {
            const dateA = new Date(a.dataNotifica || 0);
            const dateB = new Date(b.dataNotifica || 0);
            return dateB - dateA;
          });
          setNotifiche(tutteNotifiche);
        }
      } catch (error) {
        console.error('Errore nel caricamento notifiche:', error);
      }
    };

    if (user) {
      // Carica immediatamente
      fetchNotifiche();
      
      // Ricarica le notifiche ogni 10 secondi (più frequente per aggiornamenti real-time)
      const interval = setInterval(fetchNotifiche, 10000);
      
      // Ricarica quando la finestra torna in focus
      const handleFocus = () => {
        fetchNotifiche();
      };
      window.addEventListener('focus', handleFocus);
      
      // Ricarica quando la visibilità della pagina cambia (tab attiva)
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          fetchNotifiche();
        }
      };
      document.addEventListener('visibilitychange', handleVisibilityChange);
      
      return () => {
        clearInterval(interval);
        window.removeEventListener('focus', handleFocus);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
  }, [user]);

  // Chiudi i menu quando si clicca fuori
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Chiudi menu notifiche
      if (isNotificheMenuOpen) {
        const notificheButton = event.target.closest('[aria-label="Notifiche"]');
        const notificheMenu = event.target.closest('.origin-top-right');
        if (!notificheButton && !notificheMenu) {
          setIsNotificheMenuOpen(false);
        }
      }

      // Chiudi menu utente
      if (isUserMenuOpen) {
        const userButton = event.target.closest('#user-menu');
        const userMenu = event.target.closest('[role="menu"]');
        if (!userButton && !userMenu) {
          setIsUserMenuOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isNotificheMenuOpen, isUserMenuOpen]);

  // Funzione per marcare una notifica come letta
  const marcaNotificaLetta = async (notificaId) => {
    try {
      await api.put(`/notifiche/${notificaId}/letta`);
      // Aggiorna lo stato locale
      setNotifiche(prevNotifiche => 
        prevNotifiche.map(n => 
          n.id === notificaId ? { ...n, letta: true } : n
        )
      );
    } catch (error) {
      console.error('Errore nel marcare notifica come letta:', error);
    }
  };

  // Funzione per marcare tutte le notifiche come lette
  const marcaTutteComeLette = async () => {
    try {
      await api.put('/notifiche/marca-tutte-lette');
      // Aggiorna lo stato locale
      setNotifiche(prevNotifiche => 
        prevNotifiche.map(n => ({ ...n, letta: true }))
      );
    } catch (error) {
      console.error('Errore nel marcare tutte le notifiche come lette:', error);
    }
  };

  // Filtra le notifiche in base al filtro selezionato
  const getNotificheFiltrate = () => {
    switch (filtroNotifiche) {
      case 'non_lette':
        return notifiche.filter(n => !n.letta);
      case 'lette':
        return notifiche.filter(n => n.letta);
      default:
        return notifiche;
    }
  };





  // Funzione per ottenere le iniziali dell'utente per l'avatar
  const getUserInitials = () => {
    if (user && user.nome && user.cognome) {
      return `${user.nome.charAt(0)}${user.cognome.charAt(0)}`.toUpperCase();
    }
    return user?.username?.charAt(0).toUpperCase() || 'U';
  };

  const handleLogout = () => {
    logout();
  };

  // Funzione per gestire l'apertura dei menu e chiudere gli altri
  const handleMenuOpen = (menuName) => {
    setIsClientiMenuOpen(menuName === 'clienti');
    setIsQuotazioniMenuOpen(menuName === 'quotazioni');
    setIsReteMenuOpen(menuName === 'rete');
    setIsStatisticheMenuOpen(menuName === 'statistiche');
    setIsDocumentiMenuOpen(menuName === 'documenti');
    setIsGestioneMenuOpen(menuName === 'gestione');
  };

  // Funzione per chiudere tutti i menu
  const closeAllMenus = () => {
    setIsClientiMenuOpen(false);
    setIsQuotazioniMenuOpen(false);
    setIsReteMenuOpen(false);
    setIsStatisticheMenuOpen(false);
    setIsDocumentiMenuOpen(false);
    setIsGestioneMenuOpen(false);
    setIsUserMenuOpen(false);
    setIsNotificheMenuOpen(false);
  };

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-gray-50 via-white to-slate-50">
      {/* Header della dashboard */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-14 sm:h-16">
            {/* Logo e menu hamburger */}
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Link to="/nlt/" className="flex items-center">
                  <img
                    src={getLogo()}
                    alt="Logo"
                    className="h-8 sm:h-9 w-auto"
                  />
                </Link>
              </div>
              
              {/* Menu hamburger mobile */}
              <button
                className="ml-3 sm:ml-4 lg:hidden p-2 rounded-xl text-gray-600 hover:text-gray-900 hover:bg-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all duration-200"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label="Toggle mobile menu"
              >
                {isMobileMenuOpen ? (
                  <XMarkIcon className="h-6 w-6" />
                ) : (
                  <Bars3Icon className="h-6 w-6" />
                )}
              </button>
            </div>
              
            {/* Menu principale desktop */}
            <nav className="hidden lg:flex lg:items-center lg:space-x-1" onMouseLeave={closeAllMenus}>
              {/* Indicatore caricamento autorizzazioni */}
              {authLoading && (
                <div className="flex items-center px-4 py-2.5 text-sm text-gray-500 bg-white/50 rounded-xl">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                  <span className="hidden xl:inline font-medium">Caricamento menu...</span>
                </div>
              )}
              
              {/* Clienti */}
              {isPaginaAbilitata('clienti') && (
                <div className="relative group">
                  <button
                    className="flex items-center gap-1 text-gray-600 px-3 py-2 text-sm font-medium rounded-lg hover:bg-gray-100 hover:text-gray-900 group transition-colors"
                    onClick={() => handleMenuOpen(isClientiMenuOpen ? '' : 'clienti')}
                    onMouseEnter={() => handleMenuOpen('clienti')}
                  >
                    <span>Clienti</span>
                    <ChevronDownIcon className="ml-1 h-4 w-4 text-gray-500 group-hover:text-gray-600 transition-colors duration-200" />
                  </button>
                  {isClientiMenuOpen && (
                    <div className="absolute left-0 z-10 w-64 bg-white/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 ring-1 ring-gray-200/50 transition duration-150 ease-in-out">
                      <div className="py-3 rounded-2xl">
                        <div className="px-4 py-2 text-xs font-semibold text-gray-500 border-b border-gray-100/80">GESTIONE CLIENTI</div>
                        {isSottopaginaAbilitata('clienti', 'lista-clienti') && (
                          <Link 
                            to="/nlt/clienti" 
                            className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50/80 hover:text-blue-600 transition-all duration-200 rounded-xl mx-2 font-medium"
                            onClick={closeAllMenus}
                          >
                            Lista clienti
                          </Link>
                        )}
                        {isSottopaginaAbilitata('clienti', 'gestione-lead') && (
                          <Link 
                            to="/nlt/lead" 
                            className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50/80 hover:text-blue-600 transition-all duration-200 rounded-xl mx-2 font-medium"
                            onClick={closeAllMenus}
                          >
                            Gestione Lead
                          </Link>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Quotazioni */}
              {isPaginaAbilitata('quotazioni') && (
                <div className="relative group">
                  <button
                    className="flex items-center gap-1 text-gray-600 px-3 py-2 text-sm font-medium rounded-lg hover:bg-gray-100 hover:text-gray-900 group transition-colors"
                    onClick={() => handleMenuOpen(isQuotazioniMenuOpen ? '' : 'quotazioni')}
                    onMouseEnter={() => handleMenuOpen('quotazioni')}
                  >
                    <span>Quotazioni</span>
                    <ChevronDownIcon className="ml-1 h-4 w-4 text-gray-500 group-hover:text-gray-600 transition-colors duration-200" />
                  </button>
                  {isQuotazioniMenuOpen && (
                    <div className="absolute left-0 z-10 w-64 bg-white/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 ring-1 ring-gray-200/50 transition duration-150 ease-in-out">
                      <div className="py-3 rounded-2xl">
                        <div className="px-4 py-2 text-xs font-semibold text-gray-500 border-b border-gray-100/80">PREVENTIVI E VEICOLI</div>
                        {isSottopaginaAbilitata('quotazioni', 'pratiche') && (
                          <Link 
                            to="/nlt/pratiche" 
                            className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50/80 hover:text-blue-600 transition-all duration-200 rounded-xl mx-2 font-medium"
                            onClick={closeAllMenus}
                          >
                            Pratiche
                          </Link>
                        )}
                        {isSottopaginaAbilitata('quotazioni', 'lista-quotazioni') && (
                          <Link 
                            to="/nlt/quotazioni" 
                            className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50/80 hover:text-blue-600 transition-all duration-200 rounded-xl mx-2 font-medium"
                            onClick={closeAllMenus}
                          >
                            Lista quotazioni
                          </Link>
                        )}
                        {isSottopaginaAbilitata('quotazioni', 'lista-veicoli') && (
                          <Link
                            to="/nlt/veicoli"
                            className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50/80 hover:text-blue-600 transition-all duration-200 rounded-xl mx-2 font-medium"
                            onClick={closeAllMenus}
                          >
                            Lista veicoli
                          </Link>
                        )}
                        {isSottopaginaAbilitata('quotazioni', 'lista-veicoli') && (
                          <Link
                            to="/bike/bici"
                            className="block px-4 py-3 text-sm text-emerald-700 hover:bg-emerald-50/80 hover:text-emerald-800 transition-all duration-200 rounded-xl mx-2 font-medium"
                            onClick={closeAllMenus}
                          >
                            🚲 Gestione Bici (Bike)
                          </Link>
                        )}
                        {(isSottopaginaAbilitata('quotazioni', 'gestione-offerte') ||
                          isSottopaginaAbilitata('quotazioni', 'offerte-pubbliche') || 
                          isSottopaginaAbilitata('quotazioni', 'societa-noleggio')) && (
                          <div className="border-t border-gray-100/80 my-2 mx-2"></div>
                        )}
                        {(isSottopaginaAbilitata('quotazioni', 'gestione-offerte') || 
                          isSottopaginaAbilitata('quotazioni', 'offerte-pubbliche') || 
                          isSottopaginaAbilitata('quotazioni', 'societa-noleggio')) && (
                          <div className="px-4 py-2 text-xs font-semibold text-gray-500 border-b border-gray-100/80">OFFERTE E SOCIETÀ</div>
                        )}
                        {isSottopaginaAbilitata('quotazioni', 'gestione-offerte') && (
                          <Link 
                            to="/nlt/gestione-offerte-vetture" 
                            className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50/80 hover:text-blue-600 transition-all duration-200 rounded-xl mx-2 font-medium"
                            onClick={closeAllMenus}
                          >
                            Gestione Offerte
                          </Link>
                        )}
                        {isSottopaginaAbilitata('quotazioni', 'offerte-pubbliche') && (
                          <Link 
                            to="/nlt/offerte-vetture-lt" 
                            className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50/80 hover:text-blue-600 transition-all duration-200 rounded-xl mx-2 font-medium"
                            onClick={closeAllMenus}
                          >
                            Offerte Pubbliche
                          </Link>
                        )}
                        {isSottopaginaAbilitata('quotazioni', 'societa-noleggio') && (
                          <Link 
                            to="/nlt/societa-noleggio" 
                            className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50/80 hover:text-blue-600 transition-all duration-200 rounded-xl mx-2 font-medium"
                            onClick={closeAllMenus}
                          >
                            Società di noleggio
                          </Link>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Gestione */}
              {isPaginaAbilitata('gestione') && (
                <div className="relative group">
                  <button
                    className="flex items-center gap-1 text-gray-600 px-3 py-2 text-sm font-medium rounded-lg hover:bg-gray-100 hover:text-gray-900 group transition-colors"
                    onClick={() => handleMenuOpen(isGestioneMenuOpen ? '' : 'gestione')}
                    onMouseEnter={() => handleMenuOpen('gestione')}
                  >
                    <span>Gestione</span>
                    <ChevronDownIcon className="ml-1 h-4 w-4 text-gray-500 group-hover:text-gray-600 transition-colors duration-200" />
                  </button>
                  {isGestioneMenuOpen && (
                    <div className="absolute left-0 z-10 w-64 bg-white/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 ring-1 ring-gray-200/50 transition duration-150 ease-in-out">
                      <div className="py-3 rounded-2xl">
                        <div className="px-4 py-2 text-xs font-semibold text-gray-500 border-b border-gray-100/80">GESTIONE CONTRATTI</div>
                        {isSottopaginaAbilitata('gestione', 'contratti') && (
                          <Link 
                            to="/nlt/contratti" 
                            className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50/80 hover:text-blue-600 transition-all duration-200 rounded-xl mx-2 font-medium"
                            onClick={closeAllMenus}
                          >
                            Contratti
                          </Link>
                        )}
                        {isSottopaginaAbilitata('gestione', 'provvigioni-contratti') && (
                          <Link 
                            to="/nlt/provvigioni-contratti" 
                            className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50/80 hover:text-blue-600 transition-all duration-200 rounded-xl mx-2 font-medium"
                            onClick={closeAllMenus}
                          >
                            Provvigioni Contratti
                          </Link>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
                
              {/* Documenti interni */}
              {isPaginaAbilitata('documenti') && (
                <div className="relative group">
                  <button
                    className="flex items-center gap-1 text-gray-600 px-3 py-2 text-sm font-medium rounded-lg hover:bg-gray-100 hover:text-gray-900 group transition-colors"
                    onClick={() => handleMenuOpen(isDocumentiMenuOpen ? '' : 'documenti')}
                    onMouseEnter={() => handleMenuOpen('documenti')}
                  >
                    <span>Documenti interni</span>
                    <ChevronDownIcon className="ml-1 h-4 w-4 text-gray-500 group-hover:text-gray-600 transition-colors duration-200" />
                  </button>
                  {isDocumentiMenuOpen && (
                    <div className="absolute left-0 z-10 w-64 bg-white/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 ring-1 ring-gray-200/50 transition duration-150 ease-in-out">
                      <div className="py-3 rounded-2xl">
                        <div className="px-4 py-2 text-xs font-semibold text-gray-500 border-b border-gray-100/80">DOCUMENTAZIONE</div>
                        {isSottopaginaAbilitata('documenti', 'tutorial') && (
                          <Link
                            to="/nlt/guida"
                            className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50/80 hover:text-blue-600 transition-all duration-200 rounded-xl mx-2 font-medium"
                            onClick={closeAllMenus}
                          >
                            Guida
                          </Link>
                        )}
                        {isSottopaginaAbilitata('documenti', 'marketing') && (
                          <Link 
                            to="/nlt/marketing" 
                            className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50/80 hover:text-blue-600 transition-all duration-200 rounded-xl mx-2 font-medium"
                            onClick={closeAllMenus}
                          >
                            Marketing
                          </Link>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Statistiche */}
              {isPaginaAbilitata('statistiche') && (
                <div className="relative group">
                  <button
                    className="flex items-center gap-1 text-gray-600 px-3 py-2 text-sm font-medium rounded-lg hover:bg-gray-100 hover:text-gray-900 group transition-colors"
                    onClick={() => handleMenuOpen(isStatisticheMenuOpen ? '' : 'statistiche')}
                    onMouseEnter={() => handleMenuOpen('statistiche')}
                  >
                    <span>Statistiche</span>
                    <ChevronDownIcon className="ml-1 h-4 w-4 text-gray-500 group-hover:text-gray-600 transition-colors duration-200" />
                  </button>
                  {isStatisticheMenuOpen && (
                    <div className="absolute left-0 z-10 w-64 bg-white/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 ring-1 ring-gray-200/50 transition duration-150 ease-in-out">
                      <div className="py-3 rounded-2xl">
                        <div className="px-4 py-2 text-xs font-semibold text-gray-500 border-b border-gray-100/80">ANALISI DATI</div>
                        {isSottopaginaAbilitata('statistiche', 'statistiche-fatturato') && (
                          <Link 
                            to="/nlt/statistiche-fatturato" 
                            className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50/80 hover:text-blue-600 transition-all duration-200 rounded-xl mx-2 font-medium"
                            onClick={closeAllMenus}
                          >
                            Statistiche Fatturato
                          </Link>
                        )}
                        {isSottopaginaAbilitata('statistiche', 'statistiche-pratiche') && (
                          <Link 
                            to="/nlt/statistiche-pratiche" 
                            className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50/80 hover:text-blue-600 transition-all duration-200 rounded-xl mx-2 font-medium"
                            onClick={closeAllMenus}
                          >
                            Statistiche Pratiche
                          </Link>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </nav>
            
            {/* Avatar utente e notifiche */}
            <div className="flex items-center gap-2 sm:gap-3">
              {/* Campanella scadenze offerte (solo admin) */}
              <ScadenzeOfferteDropdown user={user} />
              
              {/* Campanella notifiche */}
              <div className="relative">
                <button
                  className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all"
                  onClick={() => setIsNotificheMenuOpen(!isNotificheMenuOpen)}
                  aria-label="Notifiche"
                >
                  <BellIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                  {notifiche.filter(n => !n.letta).length > 0 && (
                    <span className="absolute top-1 right-1 flex h-4 w-4 sm:h-5 sm:w-5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-4 w-4 sm:h-5 sm:w-5 bg-red-500 items-center justify-center text-white text-[10px] sm:text-xs font-bold">
                        {notifiche.filter(n => !n.letta).length > 9 ? '9+' : notifiche.filter(n => !n.letta).length}
                      </span>
                    </span>
                  )}
                </button>
                
                {/* Menu notifiche dropdown */}
                {isNotificheMenuOpen && (
                  <div className="origin-top-right absolute right-0 mt-2 w-80 sm:w-96 rounded-xl shadow-2xl bg-white ring-1 ring-black ring-opacity-5 z-50 max-h-[32rem] overflow-hidden">
                    <div className="px-4 py-3 bg-gradient-to-r from-blue-500 to-blue-600 border-b border-blue-700">
                      <h3 className="text-sm font-semibold text-white">Notifiche</h3>
                      {notifiche.filter(n => !n.letta).length > 0 && (
                        <p className="text-xs text-blue-100 mt-0.5">
                          {notifiche.filter(n => !n.letta).length} {notifiche.filter(n => !n.letta).length === 1 ? 'nuova notifica' : 'nuove notifiche'}
                        </p>
                      )}
                    </div>
                    
                    <div className="max-h-96 overflow-y-auto">
                      {notifiche.length === 0 ? (
                        <div className="px-4 py-8 text-center">
                          <BellIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                          <p className="text-sm text-gray-500">Nessuna notifica</p>
                          <p className="text-xs text-gray-400 mt-1">Sei aggiornato su tutto!</p>
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-100">
                          {notifiche.map((notifica, index) => (
                            <div
                              key={notifica.id || index}
                              className={`px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors ${!notifica.letta ? 'bg-blue-50' : ''}`}
                              onClick={async () => {
                                // Marca come letta se non lo è già
                                if (!notifica.letta) {
                                  await marcaNotificaLetta(notifica.id);
                                }
                                
                                // Gestisci click sulla notifica (naviga al link se presente)
                                if (notifica.link) {
                                  navigate(notifica.link);
                                }
                                setIsNotificheMenuOpen(false);
                              }}
                            >
                              <div className="flex items-start gap-3">
                                <div className="flex-shrink-0 mt-0.5">
                                  {!notifica.letta ? (
                                    <div className="h-2 w-2 bg-blue-500 rounded-full"></div>
                                  ) : (
                                    <div className="h-2 w-2 bg-gray-300 rounded-full"></div>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">
                                    {notifica.titolo}
                                  </p>
                                  <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                                    {notifica.descrizione || notifica.messaggio}
                                  </p>
                                  <p className="text-xs text-gray-400 mt-1">
                                    {notifica.dataNotifica ? new Date(notifica.dataNotifica).toLocaleString('it-IT', {
                                      day: '2-digit',
                                      month: 'short',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    }) : ''}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {notifiche.length > 0 && (
                      <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
                        <button 
                          className="text-xs text-blue-600 hover:text-blue-700 font-medium w-full text-center"
                          onClick={() => {
                            setIsNotificheMenuOpen(false);
                            setIsNotificheModalOpen(true);
                          }}
                        >
                          Visualizza tutte
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Informazioni utente e avatar */}
              <div className="flex items-center">
              {/* Debug info - solo in development */}
              {process.env.NODE_ENV === 'development' && ruoloMappato && (
                <div className="hidden xl:block text-xs bg-gray-100 px-2 py-1 rounded mr-3">
                  <span className="text-gray-600">Ruolo: </span>
                    <span className="font-medium text-blue-600">{ruoloMappato}</span>
                </div>
              )}
                
              <div className="relative">
                <button
                  className="flex items-center gap-1.5 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                  id="user-menu"
                  aria-haspopup="true"
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                >
                  <span className="sr-only">Menu utente</span>
                  <div className="h-9 w-9 rounded-full flex items-center justify-center bg-gray-800 text-white text-sm font-semibold">
                    {getUserInitials()}
                  </div>
                  <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                </button>

                {/* Menu utente */}
                {isUserMenuOpen && (
                  <div
                    className="origin-top-right absolute right-0 mt-2 w-64 rounded-xl shadow-xl py-1 bg-white ring-1 ring-gray-200 z-50"
                    role="menu"
                    aria-orientation="vertical"
                    aria-labelledby="user-menu"
                  >
                    <div className="px-4 py-3 border-b border-gray-100">
                      <div className="font-semibold text-gray-900 truncate">{user?.nome} {user?.cognome}</div>
                      <div className="text-gray-500 text-xs truncate">{user?.email}</div>
                      {ruoloMappato && (
                        <span className="mt-1.5 inline-block text-[11px] font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">{ruoloMappato}</span>
                      )}
                    </div>

                    <Link
                      to="/profilo"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center transition-colors"
                      role="menuitem"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <UserCircleIcon className="mr-2 h-5 w-5 text-gray-500" />
                      Profilo
                    </Link>
                    
                    <Link
                      to="/cambia-password"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center transition-colors"
                      role="menuitem"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <KeyIcon className="mr-2 h-5 w-5 text-gray-500" />
                      Cambia password
                    </Link>
                    
                    {/* Primo separatore */}
                    <div className="border-t border-gray-100 my-1"></div>
                    
                    {/* Rete Multilivello nel menu utente */}
                    {isPaginaAbilitata('rete-multilivello') && isSottopaginaAbilitata('rete-multilivello', 'dashboard-rete') && (
                          <Link 
                        to="/nlt/rete-multilivello"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center transition-colors"
                            role="menuitem"
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                        <BuildingOfficeIcon className="mr-2 h-5 w-5 text-gray-500" />
                        Rete Multilivello
                          </Link>
                        )}
                    
                    {/* Gestione Utenti nel menu utente */}
                    {isPaginaAbilitata('rete-multilivello') && isSottopaginaAbilitata('rete-multilivello', 'gestione-utenti') && (
                          <Link 
                        to="/nlt/gestione-utenti"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center transition-colors"
                            role="menuitem"
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                        <UserCircleIcon className="mr-2 h-5 w-5 text-gray-500" />
                            Gestione Utenti
                          </Link>
                        )}
                    
                    {/* Gestione Ruoli nel menu utente */}
                    {isPaginaAbilitata('rete-multilivello') && isSottopaginaAbilitata('rete-multilivello', 'gestione-ruoli') && (
                          <Link 
                        to="/nlt/gestione-ruoli"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center transition-colors"
                            role="menuitem"
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                        <KeyIcon className="mr-2 h-5 w-5 text-gray-500" />
                            Gestione Ruoli
                          </Link>
                        )}
                    
                    {/* Secondo separatore */}
                    <div className="border-t border-gray-100 my-1"></div>
                    
                    <a
                      href="https://my.wapoo.it"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center transition-colors"
                            role="menuitem"
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                      <svg className="mr-2 h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                      </svg>
                      Torna alla home
                    </a>
                    
                    <div className="border-t border-gray-100 my-1"></div>
                    
                    <button
                      className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center transition-colors"
                      role="menuitem"
                      onClick={() => {
                        setIsUserMenuOpen(false);
                        handleLogout();
                      }}
                    >
                      <ArrowLeftOnRectangleIcon className="mr-2 h-5 w-5 text-red-500" />
                      Logout
                    </button>
                  </div>
                )}
              </div>
              </div>
            </div>
          </div>
        </div>

        {/* Menu mobile */}
        {isMobileMenuOpen && (
          <div className="fixed top-16 sm:top-18 left-0 right-0 bottom-0 z-50 bg-white/95 backdrop-blur-xl shadow-2xl border-t border-gray-100/50">
            <div className="h-full overflow-y-auto">
              <div className="px-4 py-6 space-y-2">
                
                {/* Clienti Mobile */}
                {isPaginaAbilitata('clienti') && (
                  <div>
                    <button
                      className="flex items-center justify-between w-full px-4 py-3 text-base font-semibold text-gray-800 rounded-xl hover:bg-white/70 focus:outline-none transition-all duration-200"
                      onClick={() => handleMenuOpen(isClientiMenuOpen ? '' : 'clienti')}
                    >
                      <div className="flex items-center">
                        <UsersIcon className="h-5 w-5 mr-3 text-blue-500" />
                        <span>Clienti</span>
                      </div>
                      <ChevronDownIcon className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${isClientiMenuOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isClientiMenuOpen && (
                      <div className="mt-2 ml-6 space-y-1">
                        {isSottopaginaAbilitata('clienti', 'lista-clienti') && (
                          <Link 
                            to="/nlt/clienti" 
                            className="block px-4 py-3 text-sm text-gray-600 hover:bg-white/70 hover:text-blue-600 rounded-xl transition-all duration-200 font-medium"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Lista clienti
                          </Link>
                        )}
                        {isSottopaginaAbilitata('clienti', 'gestione-lead') && (
                          <Link 
                            to="/nlt/lead" 
                            className="block px-4 py-3 text-sm text-gray-600 hover:bg-white/70 hover:text-blue-600 rounded-xl transition-all duration-200 font-medium"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Gestione Lead
                          </Link>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Quotazioni Mobile */}
                {isPaginaAbilitata('quotazioni') && (
                  <div>
                    <button
                      className="flex items-center justify-between w-full px-4 py-3 text-base font-semibold text-gray-800 rounded-xl hover:bg-white/70 focus:outline-none transition-all duration-200"
                      onClick={() => handleMenuOpen(isQuotazioniMenuOpen ? '' : 'quotazioni')}
                    >
                      <div className="flex items-center">
                        <CalculatorIcon className="h-5 w-5 mr-3 text-blue-500" />
                        <span>Quotazioni</span>
                      </div>
                      <ChevronDownIcon className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${isQuotazioniMenuOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isQuotazioniMenuOpen && (
                      <div className="mt-2 ml-6 space-y-1">
                        {isSottopaginaAbilitata('quotazioni', 'pratiche') && (
                          <Link 
                            to="/nlt/pratiche" 
                            className="block px-4 py-3 text-sm text-gray-600 hover:bg-white/70 hover:text-blue-600 rounded-xl transition-all duration-200 font-medium"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Pratiche
                          </Link>
                        )}
                        {isSottopaginaAbilitata('quotazioni', 'lista-quotazioni') && (
                          <Link 
                            to="/nlt/quotazioni" 
                            className="block px-4 py-3 text-sm text-gray-600 hover:bg-white/70 hover:text-blue-600 rounded-xl transition-all duration-200 font-medium"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Lista quotazioni
                          </Link>
                        )}
                        {isSottopaginaAbilitata('quotazioni', 'lista-veicoli') && (
                          <Link 
                            to="/nlt/veicoli" 
                            className="block px-4 py-3 text-sm text-gray-600 hover:bg-white/70 hover:text-blue-600 rounded-xl transition-all duration-200 font-medium"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Lista veicoli
                          </Link>
                        )}
                        {(isSottopaginaAbilitata('quotazioni', 'gestione-offerte') || 
                          isSottopaginaAbilitata('quotazioni', 'offerte-pubbliche') || 
                          isSottopaginaAbilitata('quotazioni', 'societa-noleggio')) && (
                          <div className="border-t border-gray-100/80 my-3 mx-2"></div>
                        )}
                        {isSottopaginaAbilitata('quotazioni', 'gestione-offerte') && (
                          <Link 
                            to="/nlt/gestione-offerte-vetture" 
                            className="block px-4 py-3 text-sm text-gray-600 hover:bg-white/70 hover:text-blue-600 rounded-xl transition-all duration-200 font-medium"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Gestione Offerte
                          </Link>
                        )}
                        {isSottopaginaAbilitata('quotazioni', 'offerte-pubbliche') && (
                          <Link 
                            to="/nlt/offerte-vetture-lt" 
                            className="block px-4 py-3 text-sm text-gray-600 hover:bg-white/70 hover:text-blue-600 rounded-xl transition-all duration-200 font-medium"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Offerte Pubbliche
                          </Link>
                        )}
                        {isSottopaginaAbilitata('quotazioni', 'societa-noleggio') && (
                          <Link 
                            to="/nlt/societa-noleggio" 
                            className="block px-4 py-3 text-sm text-gray-600 hover:bg-white/70 hover:text-blue-600 rounded-xl transition-all duration-200 font-medium"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Società di noleggio
                          </Link>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Gestione Mobile */}
                {isPaginaAbilitata('gestione') && (
                  <div>
                    <button
                      className="flex items-center justify-between w-full px-4 py-3 text-base font-semibold text-gray-800 rounded-xl hover:bg-white/70 focus:outline-none transition-all duration-200"
                      onClick={() => handleMenuOpen(isGestioneMenuOpen ? '' : 'gestione')}
                    >
                      <div className="flex items-center">
                        <ClipboardDocumentListIcon className="h-5 w-5 mr-3 text-blue-500" />
                        <span>Gestione</span>
                      </div>
                      <ChevronDownIcon className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${isGestioneMenuOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isGestioneMenuOpen && (
                      <div className="mt-2 ml-6 space-y-1">
                        {isSottopaginaAbilitata('gestione', 'contratti') && (
                          <Link 
                            to="/nlt/contratti" 
                            className="block px-4 py-3 text-sm text-gray-600 hover:bg-white/70 hover:text-blue-600 rounded-xl transition-all duration-200 font-medium"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Contratti
                          </Link>
                        )}
                        {isSottopaginaAbilitata('gestione', 'provvigioni-contratti') && (
                          <Link 
                            to="/nlt/provvigioni-contratti" 
                            className="block px-4 py-3 text-sm text-gray-600 hover:bg-white/70 hover:text-blue-600 rounded-xl transition-all duration-200 font-medium"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Provvigioni Contratti
                          </Link>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Documenti interni Mobile */}
                {isPaginaAbilitata('documenti') && (
                  <div>
                    <button
                      className="flex items-center justify-between w-full px-4 py-3 text-base font-semibold text-gray-800 rounded-xl hover:bg-white/70 focus:outline-none transition-all duration-200"
                      onClick={() => handleMenuOpen(isDocumentiMenuOpen ? '' : 'documenti')}
                    >
                      <div className="flex items-center">
                        <DocumentTextIcon className="h-5 w-5 mr-3 text-blue-500" />
                        <span>Documenti interni</span>
                      </div>
                      <ChevronDownIcon className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${isDocumentiMenuOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isDocumentiMenuOpen && (
                      <div className="mt-2 ml-6 space-y-1">
                        {isSottopaginaAbilitata('documenti', 'tutorial') && (
                          <Link
                            to="/nlt/guida"
                            className="block px-4 py-3 text-sm text-gray-600 hover:bg-white/70 hover:text-blue-600 rounded-xl transition-all duration-200 font-medium"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Guida
                          </Link>
                        )}
                        {isSottopaginaAbilitata('documenti', 'marketing') && (
                          <Link 
                            to="/nlt/marketing" 
                            className="block px-4 py-3 text-sm text-gray-600 hover:bg-white/70 hover:text-blue-600 rounded-xl transition-all duration-200 font-medium"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Marketing
                          </Link>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Statistiche Mobile */}
                {isPaginaAbilitata('statistiche') && (
                  <div>
                    <button
                      className="flex items-center justify-between w-full px-4 py-3 text-base font-semibold text-gray-800 rounded-xl hover:bg-white/70 focus:outline-none transition-all duration-200"
                      onClick={() => handleMenuOpen(isStatisticheMenuOpen ? '' : 'statistiche')}
                    >
                      <div className="flex items-center">
                        <ChartBarIcon className="h-5 w-5 mr-3 text-blue-500" />
                        <span>Statistiche</span>
                      </div>
                      <ChevronDownIcon className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${isStatisticheMenuOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isStatisticheMenuOpen && (
                      <div className="mt-2 ml-6 space-y-1">
                        {isSottopaginaAbilitata('statistiche', 'statistiche-fatturato') && (
                          <Link 
                            to="/nlt/statistiche-fatturato" 
                            className="block px-4 py-3 text-sm text-gray-600 hover:bg-white/70 hover:text-blue-600 rounded-xl transition-all duration-200 font-medium"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Statistiche Fatturato
                          </Link>
                        )}
                        {isSottopaginaAbilitata('statistiche', 'statistiche-pratiche') && (
                          <Link 
                            to="/nlt/statistiche-pratiche" 
                            className="block px-4 py-3 text-sm text-gray-600 hover:bg-white/70 hover:text-blue-600 rounded-xl transition-all duration-200 font-medium"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Statistiche Pratiche
                          </Link>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Menu utente mobile - Stile Apple */}
              <div className="border-t border-gray-100/80 px-4 py-6">
                <div className="flex items-center mb-4">
                  <div className="h-12 w-12 rounded-2xl flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-600 text-white font-semibold shadow-lg">
                    {getUserInitials()}
                  </div>
                  <div className="ml-4">
                    <div className="font-semibold text-gray-900 text-base tracking-tight">{user?.nome} {user?.cognome}</div>
                    <div className="text-sm text-gray-500 font-medium">{user?.email}</div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Link
                    to="/profilo"
                    className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-white/70 rounded-xl transition-all duration-200 font-medium"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <UserCircleIcon className="mr-3 h-5 w-5 text-gray-500" />
                    Profilo
                  </Link>
                  
                  <button
                    className="flex items-center w-full px-4 py-3 text-sm text-red-600 hover:bg-red-50/80 rounded-xl transition-all duration-200 font-medium"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      handleLogout();
                    }}
                  >
                    <ArrowLeftOnRectangleIcon className="mr-3 h-5 w-5 text-red-500" />
                    Logout
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Contenuto principale con margini laterali */}
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<NltDashboard utente={user} />} />

          {/* Rotte Clienti */}
          <Route path="/clienti" element={
            <ProtectedRoute 
              user={user} 
              requiredPage="clienti" 
              requiredSubpage="lista-clienti"
            >
              <div className="max-w-[1800px] mx-auto px-4 md:px-6 mt-6">
                <ClientList />
              </div>
            </ProtectedRoute>
          } />
          <Route path="/clienti/nuovo" element={
            <ProtectedRoute 
              user={user} 
              requiredPage="clienti" 
              requiredSubpage="lista-clienti"
            >
              <div className="max-w-[1800px] mx-auto px-4 md:px-6 mt-6">
                <ClientForm />
              </div>
            </ProtectedRoute>
          } />
          <Route path="/clienti/:id" element={
            <ProtectedRoute 
              user={user} 
              requiredPage="clienti" 
              requiredSubpage="lista-clienti"
            >
              <div className="max-w-[1800px] mx-auto px-4 md:px-6 mt-6">
                <ClientDetails />
              </div>
            </ProtectedRoute>
          } />
          <Route path="/clienti/:id/modifica" element={
            <ProtectedRoute 
              user={user} 
              requiredPage="clienti" 
              requiredSubpage="lista-clienti"
            >
              <div className="max-w-[1800px] mx-auto px-4 md:px-6 mt-6">
                <ClientForm isEdit={true} />
              </div>
            </ProtectedRoute>
          } />
          
          {/* Rotte Veicoli */}
          <Route path="/veicoli" element={
            <ProtectedRoute
              user={user}
              requiredPage="quotazioni"
              requiredSubpage="lista-veicoli"
            >
              <div className="max-w-[1800px] mx-auto px-4 md:px-6 mt-6">
                <VehicleList />
              </div>
            </ProtectedRoute>
          } />

          {/* Rotte Società di Noleggio */}
          <Route path="/societa-noleggio" element={
            <ProtectedRoute 
              user={user} 
              requiredPage="quotazioni" 
              requiredSubpage="societa-noleggio"
            >
              <div className="max-w-[1800px] mx-auto px-4 md:px-6 mt-6">
                <SocietaNoleggioList />
              </div>
            </ProtectedRoute>
          } />
          <Route path="/societa-noleggio/garanzie" element={
            <ProtectedRoute 
              user={user} 
              requiredPage="quotazioni" 
              requiredSubpage="societa-noleggio"
            >
              <div className="max-w-[1800px] mx-auto px-4 md:px-6 mt-6">
                <SocietaNoleggioGaranzie />
              </div>
            </ProtectedRoute>
          } />
          
          {/* Nuova rotta per le offerte vetture */}
          <Route path="/gestione-offerte-vetture" element={
            <ProtectedRoute 
              user={user} 
              requiredPage="quotazioni" 
              requiredSubpage="gestione-offerte"
            >
              <GestioneOfferteVetturePage />
            </ProtectedRoute>
          } />
          
          {/* Rotta per la gestione categorie offerte */}
          <Route path="/gestione-offerte-vetture/categorie" element={
            <ProtectedRoute 
              user={user} 
              requiredPage="quotazioni" 
              requiredSubpage="gestione-offerte"
            >
              <GestioneCategoriePage />
            </ProtectedRoute>
          } />
          
          {/* Nuova rotta per le offerte pubbliche */}
          <Route path="/offerte-vetture-lt" element={
            <ProtectedRoute 
              user={user} 
              requiredPage="quotazioni" 
              requiredSubpage="offerte-pubbliche"
            >
              <OffertePublicPage />
            </ProtectedRoute>
          } />
          
          {/* Rotta per il preventivatore */}
          <Route path="/preventivatore" element={
            <ProtectedRoute 
              user={user} 
              requiredPage="quotazioni" 
              requiredSubpage="lista-quotazioni"
            >
              <PreventivatorePage />
            </ProtectedRoute>
          } />
          
          {/* Rotta per la gestione dei campi */}
          <Route path="/gestione-offerte-vetture/campi" element={
            <ProtectedRoute 
              user={user} 
              requiredPage="quotazioni" 
              requiredSubpage="gestione-offerte"
            >
              <div className="max-w-[1800px] mx-auto px-4 md:px-6 mt-6">
                <GestioneCampiPage />
              </div>
            </ProtectedRoute>
          } />
          
          {/* Rotta per la Guida / Base di conoscenze */}
          <Route path="/guida" element={
            <ProtectedRoute
              user={user}
              requiredPage="documenti"
              requiredSubpage="tutorial"
            >
              <GuidaPage />
            </ProtectedRoute>
          } />
          
          {/* Rotta per la pagina marketing */}
          <Route path="/marketing" element={
            <ProtectedRoute 
              user={user} 
              requiredPage="documenti" 
              requiredSubpage="marketing"
            >
              <MarketingPage />
            </ProtectedRoute>
          } />
          
          {/* Rotta per la gestione delle lead */}
          <Route path="/lead" element={
            <ProtectedRoute 
              user={user} 
              requiredPage="clienti" 
              requiredSubpage="gestione-lead"
            >
                <LeadPage />
            </ProtectedRoute>
          } />
          
          {/* Rotta per la creazione di una nuova lead */}
          <Route path="/lead/nuovo" element={
            <ProtectedRoute 
              user={user} 
              requiredPage="clienti" 
              requiredSubpage="gestione-lead"
            >
                <NewLeadPage />
            </ProtectedRoute>
          } />
          
          {/* Rotta per la gestione delle fonti e dei dettagli di provenienza */}
          <Route path="/lead/fonti-provenienza" element={
            <ProtectedRoute 
              user={user} 
              requiredPage="clienti" 
              requiredSubpage="gestione-lead"
            >
                <GestioneFontiProvenienza />
            </ProtectedRoute>
          } />
          
          {/* Rotta per la pagina Rete Multilivello */}
          <Route path="/rete-multilivello" element={
            <ProtectedRoute 
              user={user} 
              requiredPage="rete-multilivello" 
              requiredSubpage="dashboard-rete"
            >
              <ReteMultilivelloPage />
            </ProtectedRoute>
          } />

          {/* Rotta per la gestione utenti */}
          <Route path="/gestione-utenti" element={
            <ProtectedRoute 
              user={user} 
              requiredPage="rete-multilivello" 
              requiredSubpage="gestione-utenti"
            >
              <GestioneUtentiPage />
            </ProtectedRoute>
          } />

          {/* Rotta per la gestione ruoli */}
          <Route path="/gestione-ruoli" element={
            <ProtectedRoute 
              user={user} 
              requiredPage="rete-multilivello" 
              requiredSubpage="gestione-ruoli"
            >
              <GestioneRuoliPage />
            </ProtectedRoute>
          } />

          {/* Rotta per la pagina Pratiche */}
          <Route path="/pratiche" element={
            <ProtectedRoute 
              user={user} 
              requiredPage="quotazioni" 
              requiredSubpage="pratiche"
            >
              <PratichePage />
            </ProtectedRoute>
          } />

          {/* Rotta per la pagina di dettaglio pratica */}
          <Route path="/pratiche/:id" element={
            <ProtectedRoute 
              user={user} 
              requiredPage="quotazioni" 
              requiredSubpage="pratiche"
            >
              <PraticaDetailPageModulare />
            </ProtectedRoute>
          } />

          {/* Rotta per la gestione documenti (solo admin) */}
          <Route path="/pratiche/:id/gestione-documenti" element={
            <ProtectedRoute 
              user={user} 
              requiredPage="quotazioni" 
              requiredSubpage="pratiche"
            >
              <GestioneDocumentiPage />
            </ProtectedRoute>
          } />

          {/* Rotta per la pagina Contratti */}
          <Route path="/contratti" element={
            <ProtectedRoute 
              user={user} 
              requiredPage="gestione" 
              requiredSubpage="contratti"
            >
              <ContrattiPage />
            </ProtectedRoute>
          } />

          {/* Rotta per la pagina Statistiche Fatturato */}
          <Route path="/statistiche-fatturato" element={
            <ProtectedRoute 
              user={user} 
              requiredPage="statistiche" 
              requiredSubpage="statistiche-fatturato"
            >
              <StatisticheFatturatoPage />
            </ProtectedRoute>
          } />

          {/* Rotta per la pagina Statistiche Pratiche */}
          <Route path="/statistiche-pratiche" element={
            <ProtectedRoute 
              user={user} 
              requiredPage="statistiche" 
              requiredSubpage="statistiche-pratiche"
            >
              <StatistichePratichePage />
            </ProtectedRoute>
          } />

          {/* Rotta per la pagina Provvigioni Contratti */}
          <Route path="/provvigioni-contratti" element={
            <ProtectedRoute 
              user={user} 
              requiredPage="gestione" 
              requiredSubpage="provvigioni-contratti"
            >
              <ProvvigioniContrattiPage />
            </ProtectedRoute>
          } />



          {/* Rotte per quotazioni */}
          <Route path="/quotazioni" element={
            <ProtectedRoute 
              user={user} 
              requiredPage="quotazioni" 
              requiredSubpage="lista-quotazioni"
            >
              <QuotazioniPage />
            </ProtectedRoute>
          } />
        </Routes>
      </main>

      {/* Footer globale */}
      <Footer variant="minimal" />

      {/* Modale Notifiche Dettagliato */}
      {isNotificheModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Overlay */}
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
              aria-hidden="true"
              onClick={() => setIsNotificheModalOpen(false)}
            ></div>

            {/* Contenuto Modale */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-white" id="modal-title">
                      Tutte le Notifiche
                    </h3>
                    <p className="text-sm text-blue-100 mt-1">
                      {notifiche.filter(n => !n.letta).length} non lette · {notifiche.filter(n => n.letta).length} lette
                    </p>
                  </div>
                  <button
                    onClick={() => setIsNotificheModalOpen(false)}
                    className="text-white hover:text-gray-200 transition-colors"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {/* Filtri */}
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => setFiltroNotifiche('tutte')}
                    className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                      filtroNotifiche === 'tutte' 
                        ? 'bg-white text-blue-600 font-medium' 
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    Tutte ({notifiche.length})
                  </button>
                  <button
                    onClick={() => setFiltroNotifiche('non_lette')}
                    className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                      filtroNotifiche === 'non_lette' 
                        ? 'bg-white text-blue-600 font-medium' 
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    Non lette ({notifiche.filter(n => !n.letta).length})
                  </button>
                  <button
                    onClick={() => setFiltroNotifiche('lette')}
                    className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                      filtroNotifiche === 'lette' 
                        ? 'bg-white text-blue-600 font-medium' 
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    Lette ({notifiche.filter(n => n.letta).length})
                  </button>
                  {notifiche.filter(n => !n.letta).length > 0 && (
                    <button
                      onClick={marcaTutteComeLette}
                      className="ml-auto px-3 py-1.5 text-sm rounded-lg bg-white text-blue-600 hover:bg-blue-50 font-medium transition-colors"
                    >
                      Marca tutte come lette
                    </button>
                  )}
                </div>
              </div>

              {/* Lista Notifiche */}
              <div className="max-h-[60vh] overflow-y-auto">
                {getNotificheFiltrate().length === 0 ? (
                  <div className="px-6 py-12 text-center">
                    <BellIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 font-medium">Nessuna notifica da visualizzare</p>
                    <p className="text-sm text-gray-400 mt-2">
                      {filtroNotifiche === 'non_lette' && 'Tutte le notifiche sono state lette'}
                      {filtroNotifiche === 'lette' && 'Non hai ancora letto nessuna notifica'}
                      {filtroNotifiche === 'tutte' && 'Non hai ancora ricevuto notifiche'}
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {getNotificheFiltrate().map((notifica, index) => (
                      <div
                        key={notifica.id || index}
                        className={`px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                          !notifica.letta ? 'bg-blue-50' : ''
                        }`}
                        onClick={async () => {
                          // Marca come letta se non lo è già
                          if (!notifica.letta) {
                            await marcaNotificaLetta(notifica.id);
                          }
                          
                          // Gestisci click sulla notifica (naviga al link se presente)
                          if (notifica.link) {
                            setIsNotificheModalOpen(false);
                            navigate(notifica.link);
                          }
                        }}
                      >
                        <div className="flex items-start gap-4">
                          {/* Indicatore letto/non letto */}
                          <div className="flex-shrink-0 mt-1">
                            {!notifica.letta ? (
                              <div className="h-3 w-3 bg-blue-500 rounded-full ring-4 ring-blue-100"></div>
                            ) : (
                              <div className="h-3 w-3 bg-gray-300 rounded-full"></div>
                            )}
                          </div>

                          {/* Contenuto notifica */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1">
                                <h4 className="text-sm font-semibold text-gray-900">
                                  {notifica.titolo}
                                </h4>
                                <p className="text-sm text-gray-600 mt-1">
                                  {notifica.descrizione || notifica.messaggio}
                                </p>
                                
                                {/* Info aggiuntive */}
                                <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                                  <span className="flex items-center gap-1">
                                    <CalendarIcon className="h-4 w-4" />
                                    {notifica.dataNotifica ? new Date(notifica.dataNotifica).toLocaleString('it-IT', {
                                      day: '2-digit',
                                      month: 'long',
                                      year: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    }) : ''}
                                  </span>
                                  {notifica.tipo && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                      {notifica.tipo.replace(/_/g, ' ')}
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* Azioni */}
                              <div className="flex flex-col gap-2">
                                {!notifica.letta && (
                                  <button
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      await marcaNotificaLetta(notifica.id);
                                    }}
                                    className="text-xs text-blue-600 hover:text-blue-700 font-medium whitespace-nowrap"
                                  >
                                    Marca come letta
                                  </button>
                                )}
                                {notifica.link && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setIsNotificheModalOpen(false);
                                      navigate(notifica.link);
                                    }}
                                    className="text-xs text-gray-600 hover:text-gray-700 font-medium whitespace-nowrap flex items-center gap-1"
                                  >
                                    Vai al dettaglio
                                    <ArrowRightIcon className="h-3 w-3" />
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="bg-gray-50 px-6 py-3 flex justify-end gap-2">
                <button
                  onClick={() => setIsNotificheModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Chiudi
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewDashboard; 